/**
 * Error Page
 *
 * Generic error page displayed when an unhandled error occurs in the application.
 * Used by the ErrorBoundary as a fallback UI to prevent the entire application
 * from showing a blank screen or browser error.
 *
 * Features:
 * - User-friendly error message (no technical jargon)
 * - Reload button to recover by refreshing the page
 * - No AppBar dependency (works in any auth state)
 * - Optional error details in development mode for debugging
 *
 * @module pages/ErrorPage
 */

import { Box, Button, Container, Typography } from '@mui/material';

/**
 * Props for the ErrorPage component.
 */
export interface ErrorPageProps {
  /**
   * Optional error object for development display or logging.
   * In production, error details are hidden from users.
   */
  error?: Error | null;

  /**
   * Optional callback to reset the error boundary state.
   * When provided, allows recovery without a full page reload.
   * If not provided, the reload button triggers window.location.reload().
   */
  resetError?: () => void;
}

/**
 * Check if we're in development mode.
 * Uses Vite's import.meta.env.DEV flag.
 */
const isDevelopment = import.meta.env.DEV;

/**
 * ErrorPage - Generic error page component.
 *
 * Provides a user-friendly error display with recovery options.
 * Does not include AppBar as the error may have occurred before auth state
 * was determined, or the AppBar itself may have crashed.
 *
 * Used for:
 * - Error boundary fallback when a component crashes
 * - Direct navigation to /error route
 *
 * No guards applied - accessible in any auth state.
 *
 * @example
 * ```tsx
 * // Used by ErrorBoundary
 * <ErrorPage error={error} resetError={handleReset} />
 *
 * // Standalone usage
 * <ErrorPage />
 * ```
 */
export function ErrorPage({ error, resetError }: ErrorPageProps) {
  /**
   * Handle reload button click.
   * If resetError callback is provided, call it to reset the error boundary.
   * Otherwise, trigger a full page reload to give the app a fresh start.
   */
  const handleReload = () => {
    if (resetError) {
      resetError();
    } else {
      window.location.reload();
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          padding: 3,
          textAlign: 'center',
        }}
        data-testid="error-page"
      >
        {/* Error Heading */}
        <Typography
          variant="h4"
          component="h1"
          gutterBottom
          data-testid="error-heading"
        >
          Something went wrong
        </Typography>

        {/* User-Friendly Message */}
        <Typography
          variant="body1"
          data-testid="error-message"
          sx={{
            color: 'text.secondary',
            mb: 4,
            maxWidth: 400,
          }}
        >
          An unexpected error occurred. Please try reloading the page.
        </Typography>

        {/* Reload Button */}
        <Button
          variant="contained"
          onClick={handleReload}
          data-testid="error-reload-button"
        >
          Reload
        </Button>

        {/* Development Mode: Error Details */}
        {isDevelopment && error && (
          <Box
            sx={{
              mt: 4,
              p: 2,
              bgcolor: 'grey.100',
              borderRadius: 1,
              width: '100%',
              maxWidth: 500,
              textAlign: 'left',
            }}
            data-testid="error-details"
          >
            <Typography
              variant="subtitle2"
              color="error"
              sx={{ fontFamily: 'monospace', mb: 1 }}
            >
              {error.name}: {error.message}
            </Typography>
            {error.stack && (
              <Typography
                variant="caption"
                component="pre"
                sx={{
                  fontFamily: 'monospace',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: 'text.secondary',
                  m: 0,
                }}
              >
                {error.stack}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Container>
  );
}
